import * as React from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import { Avatar } from '@mui/material';
import AppBarContext from '../../Contexts/AppBarContext/AppBarContext';
import Session from 'supertokens-auth-react/recipe/session';
import { signOut } from 'supertokens-auth-react/recipe/emailpassword';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import Badge from '@mui/material/Badge';
import { Task, BeachAccess, RadioButtonChecked } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UserContext from '../../Contexts/UserContext/UserContext';
import { ArbeitszeitService } from '../../services/openapi';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import i18n from '../../locales/i18n';
import APIWrapperContext from '../../services/APIWrapperContext';
import { Chip, Tooltip } from '@mui/material';

const drawerWidth = 440;

const AppBarStyled = styled(MuiAppBar, {
  shouldForwardProp: (prop) => true,
})(({ theme }) => ({
  width: '100%',
  marginLeft: 0,
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.enteringScreen,
  }),
}));

interface NotificationItem {
  type: 'vacation' | 'document';
  count: number;
  message: string;
  link: string;
}

export default function AppBar() {
  const { additionalUserdata, reset_NewDocumentNotifications } = React.useContext(UserContext);
  const { openDrawer, setOpenDrawer } = React.useContext(AppBarContext);
  const { call_backend_api } = React.useContext(APIWrapperContext);
  const navigate = useNavigate();
  const sessionContext = Session.useSessionContext();
  const [languageSelectModalState, setLanguageSelectModalState] = React.useState(false);

  // Notification state
  const [notifications, setNotifications] = React.useState<{
    vacation: number;
    document: number;
    total: number;
  }>({ vacation: 0, document: 0, total: 0 });

  // Stempel status state
  const [isEingestempelt, setIsEingestempelt] = React.useState<boolean>(false);
  const [stempelInfo, setStempelInfo] = React.useState<{
    startTime: string;
    bauvorhaben: string;
  } | null>(null);
  const [isAuthorizedForStempeln, setIsAuthorizedForStempeln] = React.useState<boolean>(true);

  // Menu state
  const [notificationAnchorEl, setNotificationAnchorEl] = React.useState<null | HTMLElement>(null);
  const [profileAnchorEl, setProfileAnchorEl] = React.useState<null | HTMLElement>(null);

  const notificationOpen = Boolean(notificationAnchorEl);
  const profileOpen = Boolean(profileAnchorEl);

  // Fetch notifications from user metadata
  React.useEffect(() => {
    if (!sessionContext.loading && sessionContext.doesSessionExist && additionalUserdata) {
      const vacationNotifications = additionalUserdata.vacation_notifications || 0;
      const documentNotifications = Number(additionalUserdata.notifications) || 0;

      setNotifications({
        vacation: vacationNotifications,
        document: documentNotifications,
        total: vacationNotifications + documentNotifications,
      });
    }
  }, [sessionContext.loading, sessionContext, additionalUserdata]);

  // Reset authorization state when user changes
  const userId = !sessionContext.loading && sessionContext.doesSessionExist ? sessionContext.userId : null;
  React.useEffect(() => {
    setIsAuthorizedForStempeln(true);
  }, [userId, additionalUserdata]);

  // Check stempel status
  const checkStempelStatus = React.useCallback(() => {
    if (!sessionContext.loading && sessionContext.doesSessionExist && isAuthorizedForStempeln) {
      const today = new Date();
      const year = today.getFullYear();
      const month = (today.getMonth() + 1).toString().padStart(2, '0');
      const day = today.getDate().toString().padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;

      call_backend_api(
        ArbeitszeitService.execStempelnArbeitzeitStempeleintragGet(),
        (response: any) => {
          // Check if there's an ongoing work entry (has start time but no end time or end time is after current time)
          const ongoingEntry = response.find((entry: any) => {
            const startTime = new Date(entry.beginn);
            const endTime = entry.ende ? new Date(entry.ende) : null;
            const now = new Date();

            return startTime <= now && (!endTime || endTime > now);
          });
          console.log('Ongoing entry:', ongoingEntry);
          if (ongoingEntry) {
            setIsEingestempelt(true);
            setStempelInfo({
              startTime: new Date(ongoingEntry.beginn).toLocaleTimeString('de-DE', {
                hour: '2-digit',
                minute: '2-digit'
              }),
              bauvorhaben: ongoingEntry.bvh?.bvh_name || 'Unbekannt'
            });
          } else {
            setIsEingestempelt(false);
            setStempelInfo(null);
          }
        },
        (error: any) => {
          console.error('Failed to check stempel status:', error);
          if (error.status === 403) {
            console.warn('User not authorized for stempel status, disabling checks.');
            setIsAuthorizedForStempeln(false);
          }
          setIsEingestempelt(false);
          setStempelInfo(null);
        },
        undefined,
        false
      );
    }
  }, [sessionContext, call_backend_api, isAuthorizedForStempeln]);

  // Fetch stempel status on component mount and set up periodic updates
  React.useEffect(() => {
    checkStempelStatus();

    // Update stempel status every 5 minutes
    const interval = setInterval(checkStempelStatus, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [checkStempelStatus]);

  // Notification handlers
  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    if (notifications.total > 0) {
      setNotificationAnchorEl(event.currentTarget);
    }
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleNotificationItemClick = (link: string) => {
    navigate(link);
    handleNotificationClose();
    if (link === '/') {
      reset_NewDocumentNotifications();
    }
  };

  // Profile menu handlers
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    localStorage.removeItem('userRoles');
    await signOut();
    window.location.href = '/';
  };

  // Notification items
  const notificationItems: NotificationItem[] = [
    {
      type: 'vacation' as const,
      count: notifications.vacation,
      message:
        notifications.vacation === 1
          ? '1 ausstehender Urlaubsantrag'
          : `${notifications.vacation} ausstehende Urlaubsanträge`,
      link: '/zeiterfassung/urlaub?tab=offeneAntraege',
    },
    {
      type: 'document' as const,
      count: notifications.document,
      message:
        notifications.document === 1
          ? '1 neues Dokument zur Überprüfung'
          : `${notifications.document} neue Dokumente zur Überprüfung`,
      link: '/',
    },
  ].filter((item) => item.count > 0);

  return (
    <>
      <AppBarStyled position='fixed' color={'backgroundboe' as any}>
        <Toolbar>
          {!sessionContext.loading && sessionContext.doesSessionExist && (
            <IconButton
              size='large'
              edge='start'
              color='inherit'
              aria-label='open drawer'
              onClick={() => setOpenDrawer(true)}
              sx={{ mr: 2, ...(openDrawer ? { display: 'none' } : {}) }}>
              <MenuIcon />
            </IconButton>
          )}

          <IconButton sx={{ m: 0 }} href='/' id='logo'>
            <Avatar
              alt='Logo'
              src='/logo.svg'
              variant='square'
              sx={{ height: '40px', width: '40px', marginRight: '24px' }}
            />
          </IconButton>

          <Typography
            variant='h6'
            noWrap
            component='div'
            sx={{ display: { xs: 'none', sm: 'block' } }}
          />

          <Box sx={{ flexGrow: 1 }} />

          {!sessionContext.loading && sessionContext.doesSessionExist && (
            <Box sx={{ display: { xs: 'flex', md: 'flex' }, alignItems: 'center', gap: 1 }}>
              {/* Stempel Status Indicator */}
              {isEingestempelt && stempelInfo && (
                <Tooltip
                  title={`Eingestempelt seit ${stempelInfo.startTime} - ${stempelInfo.bauvorhaben}`}
                  arrow
                >
                  <Chip
                    icon={<RadioButtonChecked />}
                    label={`${stempelInfo.startTime}`}
                    color="success"
                    variant="filled"
                    size="small"
                    sx={{
                      backgroundColor: '#4caf50',
                      color: 'white',
                      fontWeight: 'bold',
                      '& .MuiChip-icon': {
                        color: 'white'
                      }
                    }}
                  />
                </Tooltip>
              )}
              <IconButton
                size='large'
                edge='end'
                aria-label='notifications'
                aria-controls='notification-menu'
                aria-haspopup='true'
                onClick={handleNotificationClick}
                color='inherit'
                sx={{ mr: 1 }}
                disabled={notifications.total === 0}>
                {notifications.total > 0 ? (
                  <Badge badgeContent={notifications.total} color='primary'>
                    <NotificationsActiveIcon />
                  </Badge>
                ) : (
                  <NotificationsActiveIcon />
                )}
              </IconButton>

              <IconButton
                size='large'
                edge='end'
                aria-label='account of current user'
                aria-controls='profile-menu'
                aria-haspopup='true'
                onClick={handleProfileMenuOpen}
                color='inherit'>
                <AccountCircle />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBarStyled>

      {/* Notifications Menu - Only shown when there are notifications */}
      {notifications.total > 0 && (
        <Menu
          anchorEl={notificationAnchorEl}
          open={notificationOpen}
          onClose={handleNotificationClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{
            '& .MuiPaper-root': {
              width: 300,
              maxWidth: '100%',
            },
          }}>
          {notificationItems.map((item, index) => (
            <MenuItem
              key={index}
              onClick={() => handleNotificationItemClick(item.link)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                py: 1.5,
              }}>
              <Badge badgeContent={item.count} color='primary'>
                {item.type === 'vacation' ? <BeachAccess /> : <Task />}
              </Badge>
              <Typography variant='body2'>{item.message}</Typography>
            </MenuItem>
          ))}
        </Menu>
      )}

      {/* Profile Menu */}
      <Menu
        anchorEl={profileAnchorEl}
        open={profileOpen}
        onClose={handleProfileMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem
          onClick={() => {
            navigate('/user_options');
            handleProfileMenuClose();
          }}>
          Mein Konto
        </MenuItem>

        <MenuItem onClick={() => setLanguageSelectModalState(true)}>Sprache wechseln</MenuItem>

        <MenuItem onClick={handleLogout}>Logout</MenuItem>
      </Menu>
      <Modal open={languageSelectModalState} onClose={() => { handleProfileMenuClose(); setLanguageSelectModalState(false); }}>
        <Box style={{ display: 'flex', flexDirection: 'column', padding: '20px', gap: '10px', backgroundColor: 'white', borderRadius: '8px', maxWidth: '400px', margin: 'auto', marginTop: '20vh' }}>
          <Typography variant='h6'>Sprache wählen</Typography>
          <Button onClick={() => { handleProfileMenuClose(); setLanguageSelectModalState(false); i18n.changeLanguage('de'); }}>Deutsch</Button>
          <Button onClick={() => { handleProfileMenuClose(); setLanguageSelectModalState(false); i18n.changeLanguage('en'); }}>English</Button>
        </Box>
      </Modal>
    </>
  );
}
